import React, { useState, useEffect } from 'react';
import { Box, Typography, List, Pagination, Stack, Button, Paper } from '@mui/material';
import { useAppContext } from '../../contexts/AppContext';
import { usePaperFilters } from '../../hooks/usePaperFilters';
import FilterBar from './filters/FilterBar';
import PaperCard from './PaperCard';
import LoadingOverlay from '../common/LoadingOverlay';

function PapersList() {
  const { papers, selectedPapers, setSelectedPapers, searchLoading } = useAppContext();
  const { filters, setFilters, filteredPapers } = usePaperFilters(papers);
  const [currentPage, setCurrentPage] = useState(1);
  const papersPerPage = 10;

  // Reset to page 1 whenever the filtered papers list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredPapers.length]);

  const handleTogglePaper = (paper) => {
    const currentIndex = selectedPapers.findIndex(p => p.paperId === paper.paperId);
    const newSelectedPapers = [...selectedPapers];

    if (currentIndex === -1) {
      newSelectedPapers.push(paper);
    } else {
      newSelectedPapers.splice(currentIndex, 1);
    }

    setSelectedPapers(newSelectedPapers);
  };

  const handleOpenPaper = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (searchLoading) {
    return <LoadingOverlay message="Searching for papers..." />;
  }

  // Always show at least 2 pages for testing pagination
  const totalPages = Math.max(2, Math.ceil(filteredPapers.length / papersPerPage));
  // Always show pagination for testing
  const showPagination = true;

  return (
    <Box>
      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" padding={1}>
          Search Results ({filteredPapers.length} papers)
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            {selectedPapers.length} papers selected
          </Typography>
          {filteredPapers.length > 0 && (
            <Typography variant="body2" color="text.secondary" padding={1}>
              Showing {Math.min((currentPage - 1) * papersPerPage + 1, filteredPapers.length)}-
              {Math.min(currentPage * papersPerPage, filteredPapers.length)} of {filteredPapers.length}
            </Typography>
          )}
        </Box>
      </Box>

      <List>
        {filteredPapers
          .slice((currentPage - 1) * papersPerPage, currentPage * papersPerPage)
          .map((paper, index) => (
            <PaperCard
              key={paper.paperId}
              paper={paper}
              index={(currentPage - 1) * papersPerPage + index}
              selected={selectedPapers.some(p => p.paperId === paper.paperId)}
              onSelect={handleTogglePaper}
              onOpenUrl={handleOpenPaper}
            />
          ))}
      </List>

      {/* Always show pagination for testing */}
      <Paper elevation={0} sx={{ p: 2, mt: 3, mb: 2, border: '1px solid var(--neutral-200)', borderRadius: 'var(--border-radius-md)', backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
            siblingCount={1}
            size="medium"
            showFirstButton
            showLastButton
          />
        </Stack>
      </Paper>
    </Box>
  );
}

export default PapersList; 